import { Component, Inject, Injector, Input } from '@angular/core';
import { IsActiveMatchOptions } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { groupedReports, MENU_DEF, MenuDef, MenuItem, ReportGroup, VisibleReportsCache } from 'src/app/common/menu-def.provider';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { ExperimentProps } from 'src/app/services/growthbook/types';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { Logout } from 'src/app/store/auth/auth.actions';
import { AuthState } from 'src/app/store/auth/auth.state';
import { AuthCompany, AuthUser } from 'src/models';
import { provideAmpTrackCtxFromUrl } from 'src/app/services/tracking/amp-track';

@UntilDestroy()
@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  providers: [provideAmpTrackCtxFromUrl()],
})
export class SidenavComponent {

  customRouterLinkActiveOptions: IsActiveMatchOptions = { paths: 'exact', fragment: 'exact', queryParams: 'ignored', matrixParams: 'ignored' };

  @Select(AuthState.company) company$: Observable<AuthCompany>;
  @Select(AuthState.user) user$: Observable<AuthUser>;

  @Input() activeUrl: string;

  // Reports menu grouping - mobile always shows vertical layout (Variant A style)
  showGroupedReports = false;
  groupedReports = groupedReports;
  private reportsMenuExperiment: ExperimentProps | null = null;
  private visibleReportsCache: VisibleReportsCache;

  constructor(
    private store: Store,
    @Inject(MENU_DEF) public menuDef: MenuDef,
    public injector: Injector,
    private growthBook: GrowthBookService,
    private segmentService: SegmentService,
    private translate: TranslateService,
  ) {
    this.visibleReportsCache = new VisibleReportsCache(this.injector);

    // Reports menu grouping A/B test - mobile shows vertical layout for both variants
    this.growthBook.getFeature<ExperimentProps>('reports-menu-grouping-experiment')
      .pipe(untilDestroyed(this)).subscribe((experiment: ExperimentProps | null) => {
        const isValidVariation = experiment?.variationid === 'A' || experiment?.variationid === 'B';
        this.showGroupedReports = isValidVariation;
        this.reportsMenuExperiment = isValidVariation ? experiment : null;
      });
  }

  logout() {
    this.store.dispatch(new Logout(true));
  }

  // Called from template expressions, so this runs on every change-detection pass.
  // The cache keeps both the flag evaluations and the array identity stable.
  getVisibleReports(group: ReportGroup, company: AuthCompany): MenuItem[] {
    return this.visibleReportsCache.get(group, company);
  }

  hasVisibleReports(group: ReportGroup, company: AuthCompany): boolean {
    return this.getVisibleReports(group, company).length > 0;
  }

  /**
   * Get report group by stable key identifier
   */
  getGroupByKey(key: string): ReportGroup | undefined {
    return this.groupedReports.find(g => g.key === key);
  }

  /**
   * Track experiment view when Reports expansion panel is opened
   */
  onExpansionPanelOpened(item: any): void {
    if (item.name === 'header.reports') {
      // Track Reports menu dropdown click
      this.segmentService.trackInAppEvents('Reports Main Menu Dropdown Clicked', {
        menuclickedlocation: document.title,
        menuclickedurl: window.location.href,
      });

      // Track experiment view if user is in the experiment
      const variationId = this.reportsMenuExperiment?.variationid;
      if (variationId === 'A' || variationId === 'B') {
        const variationName = variationId === 'A'
          ? 'Grouped Reports menu - vertical layout'
          : 'Grouped Reports menu - horizontal layout';

        this.segmentService.trackInAppEvents('Experiment Viewed', {
          experimentid: 'CF-STAFF-144644',
          experimentname: 'Reports Menu Grouping Experiment',
          variationid: this.reportsMenuExperiment.variationid,
          variationname: variationName,
        });
      }
    }
  }

  /**
   * Track when a report menu item is clicked
   */
  onReportItemClick(report: MenuItem, group: ReportGroup): void {
    const reportName = this.translate.instant(report.name);
    const groupName = this.translate.instant(group.name);

    this.segmentService.trackInAppEvents('Reports Menu Item Clicked', {
      reportitem: reportName,
      reportgroup: groupName,
      menuclickedlocation: document.title,
      menuclickedurl: window.location.href,
    });
  }
}
